import {
  WorkflowPageConfig,
  WorkflowRoutineConfig,
  newGUID,
} from '@gripp/shared-logic';
import { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { View } from 'react-native';
import { object as YupObject, array, boolean, string } from 'yup';
import { RoutineAdminForm, RoutineAdminFormProps } from '../../types';
import {
  MAX_IDP_MANAGED_FIELDS,
  countIdpManagedQuestions,
} from './idpQuestions';
import { WorkflowAdminPage } from './workflowAdminPage';

export const WorkflowAdminSchema = {
  pages: array()
    .of(
      YupObject({
        instructions: string(),
        questions: array()
          .of(
            YupObject({
              instructions: string().required(),
              idpManaged: boolean().optional(),
              // The backend planner drops any prompt that TRIMS to empty
              // (finding: an unprompted managed field extracts nothing), so a
              // managed field needs a non-blank prompt to save — required()
              // alone would let "   " through to a field that never extracts.
              idpPrompt: string().when('idpManaged', {
                is: true,
                then: (schema) =>
                  schema
                    .required()
                    .test(
                      'idp-prompt-not-blank',
                      'idpPromptRequired',
                      (value) => !!value?.trim()
                    ),
                otherwise: (schema) => schema.optional(),
              }),
            })
          )
          .required()
          .min(1),
      })
    )
    .required()
    .min(1)
    // Mirrors the API enqueue planner's own MAX_IDP_FIELDS reject — the
    // editor's front door to the same hard limit (finding #19).
    .test(
      'idp-managed-cap',
      'idpManagedCapExceeded',
      (pages) =>
        countIdpManagedQuestions(pages as unknown as WorkflowPageConfig[]) <=
        MAX_IDP_MANAGED_FIELDS
    ),
};

export const WorkflowAdminForm: RoutineAdminForm<WorkflowRoutineConfig> = (
  props: RoutineAdminFormProps<WorkflowRoutineConfig>
) => {
  const methods = useForm();
  const { control } = props;
  const { fields, append } = useFieldArray({
    control,
    name: 'pages',
  });
  const DefaultPage: WorkflowPageConfig = {
    id: newGUID(),
    name: 'Page 1',
    order: 1,
    instructions: '',
    questions: [],
  };

  //load initial pages
  useEffect(() => {
    if (fields.length > 0) return;
    append(DefaultPage);
  }, []);

  return (
    <FormProvider {...methods}>
      <View>
        {fields.map((field, index) => (
          <WorkflowAdminPage
            key={`page_${field.id}`}
            index={index}
            page={field as WorkflowPageConfig}
            control={control}
            mediaPicker={props.mediaPicker}
          />
        ))}
      </View>
    </FormProvider>
  );
};
